import {
  ApiException,
  ProspectCodeRequest,
  ProblemDetails
} from '@shared/webapi/MIF.Subscription.WebApi';
import {
  ClientAuthRequest,
  ClientAuthErrorCode,
  RequestCodeClientAuthError,
  RequestCodeClientProblemDetails,
  ValidateCodeClientAuthError
} from './client.models';

export function isProspectCodeRequest(request: ClientAuthRequest): request is ProspectCodeRequest {
  return 'email' in request && 'phoneNumber' in request;
}

export function parseRequestCodeClientError(error: unknown): RequestCodeClientAuthError {
  const problemDetails = getProblemDetails(error);
  const errorCode = getClientAuthErrorCode(problemDetails);

  return {
    detail: problemDetails?.detail ?? 'Une erreur est survenue.',
    errorCode,
    lockoutEnd: errorCode === ClientAuthErrorCode.AccessBlocked ? (problemDetails?.lockoutEnd ?? null) : null,
    remainingAttempts: null,
    status: problemDetails?.status ?? null,
    title: problemDetails?.title ?? null
  };
}

export function parseValidateCodeClientError(error: unknown): ValidateCodeClientAuthError {
  const problemDetails = getProblemDetails(error);
  const errorCode = getClientAuthErrorCode(problemDetails);

  return {
    detail: problemDetails?.detail ?? 'Une erreur est survenue.',
    errorCode,
    lockoutEnd: errorCode === ClientAuthErrorCode.AccessBlocked ? (problemDetails?.lockoutEnd ?? null) : null,
    remainingAttempts: errorCode === ClientAuthErrorCode.InvalidCode ? (problemDetails?.remainingAttempts ?? null) : null,
    status: problemDetails?.status ?? null,
    title: problemDetails?.title ?? null
  };
}

function getProblemDetails(error: unknown): RequestCodeClientProblemDetails | null {
  const apiError = getApiProblemSource(error);

  if (!apiError) {
    return null;
  }

  return ProblemDetails.fromJS(apiError) as RequestCodeClientProblemDetails;
}

function getApiProblemSource(error: unknown): Record<string, unknown> | null {
  if (ApiException.isApiException(error) && error.response) {
    try {
      return JSON.parse(error.response) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  if (isObject(error) && isObject(error.error)) {
    return error.error;
  }

  if (isObject(error)) {
    return error;
  }

  return null;
}

function getClientAuthErrorCode(problemDetails: RequestCodeClientProblemDetails | null): ClientAuthErrorCode | null {
  const errorCode = problemDetails?.errorCode;

  if (errorCode === ClientAuthErrorCode.InvalidCredentials) {
    return ClientAuthErrorCode.InvalidCredentials;
  }

  if (errorCode === ClientAuthErrorCode.InvalidCode) {
    return ClientAuthErrorCode.InvalidCode;
  }

  if (errorCode === ClientAuthErrorCode.AccessBlocked) {
    return ClientAuthErrorCode.AccessBlocked;
  }

  if (errorCode === ClientAuthErrorCode.CodeDeliveryFailed) {
    return ClientAuthErrorCode.CodeDeliveryFailed;
  }

  if (
    problemDetails?.remainingAttempts !== undefined ||
    problemDetails?.title === 'Invalid Code' ||
    problemDetails?.detail === 'The provided code is invalid.'
  ) {
    return ClientAuthErrorCode.InvalidCode;
  }

  return null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
